import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Check, User, Building2, HardHat, Rocket, Briefcase, Gem, Award, Crown, Sparkles, ArrowUpRight } from 'lucide-react';
import { PackageInquiryDialog } from '@/components/advertise/PackageInquiryDialog';

interface PackageRow {
  id: string;
  name: string;
  tier_label: string | null;
  audience: 'individual' | 'agency' | 'developer';
  price_etb: number;
  price_currency: 'ETB' | 'USD';
  duration_days: number;
  billing_cycle: string;
  features: any;
  quotas: Record<string, number | string>;
  perks: string[];
  allows_banner_ads: boolean;
  banner_ad_quota: number;
  cta_mode: 'inquire' | 'checkout';
  is_popular: boolean;
  is_active: boolean;
  sort_order: number;
}

const ICONS_BY_TIER: Record<string, any> = {
  starter: Rocket,
  business: Briefcase,
  titanium: Gem,
  'titanium plus': Crown,
  'value booster': Sparkles,
  'brand maximizer': Crown,
  'premium developer': Award,
  'starter pack': Rocket,
};

function iconForPackage(p: PackageRow) {
  const key = (p.tier_label || p.name).toLowerCase();
  for (const k of Object.keys(ICONS_BY_TIER)) {
    if (key.includes(k)) return ICONS_BY_TIER[k];
  }
  return Award;
}

function billingLabel(cycle: string, days: number) {
  if (cycle === 'monthly_billed_annually') return 'Per Month (Billed Annually)';
  if (cycle === 'monthly') return 'Per Month';
  if (cycle === 'annually') return 'Per Year';
  if (cycle === 'one_time') return `One-time · ${days} days`;
  return `/${days} days`;
}

function PackageCard({ pkg, onSelect }: { pkg: PackageRow; onSelect: (p: PackageRow) => void; }) {
  const Icon = iconForPackage(pkg);
  const quotaEntries = Object.entries(pkg.quotas || {});
  const legacyFeatures = Array.isArray(pkg.features) ? (pkg.features as string[]).filter((f) => typeof f === 'string') : [];
  const showLegacy = quotaEntries.length === 0 && pkg.perks.length === 0 && legacyFeatures.length > 0;

  return (
    <Card className={`relative flex flex-col h-full transition-shadow hover:shadow-lg rounded-2xl ${
      pkg.is_popular ? 'border-2 border-primary shadow-md bg-primary/[0.02]' : 'border'
    }`}>
      {pkg.is_popular && (
        <Badge className="absolute -top-3 right-4 bg-orange-500 hover:bg-orange-500 text-white px-3 py-1 gap-1">
          <Sparkles className="h-3 w-3" /> Most Popular
        </Badge>
      )}
      <CardHeader className="pb-2 pt-6">
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <Icon className="h-6 w-6 text-primary" />
        </div>
        <CardTitle className="text-lg font-semibold">{pkg.tier_label || pkg.name}</CardTitle>
        <div className="mt-3">
          <p className="text-xs text-muted-foreground">Starting From</p>
          <p className="text-2xl font-bold text-foreground mt-0.5">
            {pkg.price_currency} {Number(pkg.price_etb).toLocaleString()}
          </p>
          <p className="text-xs text-primary mt-0.5">{billingLabel(pkg.billing_cycle, pkg.duration_days)}</p>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col flex-1 pt-2">
        <p className="text-sm font-semibold mb-3 text-foreground">What's included</p>
        <ul className="space-y-2 flex-1 mb-5">
          {quotaEntries.map(([label, val]) => (
            <li key={label} className="flex items-start gap-2 text-sm">
              <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary/15">
                <Check className="h-3 w-3 text-primary" />
              </span>
              <span className="text-muted-foreground">
                <strong className="text-foreground">{val}</strong> {label}
              </span>
            </li>
          ))}
          {pkg.perks.map((perk) => (
            <li key={perk} className="flex items-start gap-2 text-sm">
              <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary/15">
                <Check className="h-3 w-3 text-primary" />
              </span>
              <span className="text-foreground">{perk}</span>
            </li>
          ))}
          {pkg.allows_banner_ads && pkg.banner_ad_quota > 0 && (
            <li className="flex items-start gap-2 text-sm">
              <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary/15">
                <Check className="h-3 w-3 text-primary" />
              </span>
              <span className="text-muted-foreground">
                <strong className="text-foreground">{pkg.banner_ad_quota}</strong> Banner Ad Campaign{pkg.banner_ad_quota > 1 ? 's' : ''}
              </span>
            </li>
          )}
          {showLegacy && legacyFeatures.map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
              <Check className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
        <Button
          className={`w-full ${pkg.is_popular ? '' : 'bg-primary/10 text-primary hover:bg-primary/20'}`}
          variant={pkg.is_popular ? 'default' : 'ghost'}
          onClick={() => onSelect(pkg)}
        >
          Request Package
        </Button>
        <button className="mt-2 text-xs text-primary hover:underline inline-flex items-center justify-center gap-1">
          View More Details <ArrowUpRight className="h-3 w-3" />
        </button>
      </CardContent>
    </Card>
  );
}

export default function Advertise() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [inquiryPkg, setInquiryPkg] = useState<PackageRow | null>(null);

  const { data: packages, isLoading } = useQuery({
    queryKey: ['advertising-packages-public'],
    queryFn: async () => {
      const { data } = await supabase
        .from('advertising_packages' as any)
        .select('*')
        .eq('is_active', true)
        .order('audience').order('sort_order');
      return (data as unknown as PackageRow[]) || [];
    },
    staleTime: 60_000,
  });

  const grouped = useMemo(() => {
    const out: Record<string, PackageRow[]> = { individual: [], agency: [], developer: [] };
    (packages || []).forEach((p) => {
      const aud = (p.audience || 'individual').toLowerCase();
      if (out[aud]) out[aud].push(p);
    });
    return out;
  }, [packages]);

  // Restore preselected package from URL (e.g. after returning from /login).
  // ?pkg=<id>&mode=checkout|inquire — only opens once the user is signed in,
  // the packages list has loaded, and the requested package is valid.
  useEffect(() => {
    if (!user || !packages) return;
    const pkgId = searchParams.get('pkg');
    const mode = searchParams.get('mode');
    if (!pkgId) return;

    // Always clear the params after one attempt so a bad URL is not sticky
    // and a refresh does not keep re-opening / re-toasting.
    const cleanup = () => {
      const next = new URLSearchParams(searchParams);
      next.delete('pkg');
      next.delete('mode');
      setSearchParams(next, { replace: true });
    };

    // 1) Validate UUID shape — guards against tampered or malformed URLs.
    const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRe.test(pkgId)) {
      toast({
        variant: 'destructive',
        title: 'Invalid package link',
        description: 'The package reference in this link is not valid.',
      });
      cleanup();
      return;
    }

    // 2) Validate mode if supplied.
    if (mode && mode !== 'checkout' && mode !== 'inquire') {
      toast({
        variant: 'destructive',
        title: 'Invalid package link',
        description: 'Unknown action — please pick a package below.',
      });
      cleanup();
      return;
    }

    // 3) The package must be in the public (is_active = true) list.
    //    `packages` already filters to active rows server-side.
    const pkg = packages.find((p) => p.id === pkgId);
    if (!pkg) {
      toast({
        variant: 'destructive',
        title: 'Package unavailable',
        description: 'This package is no longer offered. Please choose another below.',
      });
      cleanup();
      return;
    }

    // 4) Honor the mode the user originally clicked, but only if the
    //    package itself supports it (defence in depth — pkg.cta_mode wins).
    setInquiryPkg(pkg);
    cleanup();
  }, [user, packages, searchParams, setSearchParams, toast]);

  const onSelect = (p: PackageRow) => {
    if (!user) {
      toast({
        title: 'Sign in required',
        description: 'Please sign in to inquire about a package.',
      });
      const redirect = `/advertise?pkg=${encodeURIComponent(p.id)}`;
      navigate(`/login?redirect=${encodeURIComponent(redirect)}`);
      return;
    }
    setInquiryPkg(p);
  };


  const renderGrid = (rows: PackageRow[], cols: string, sectionLabel: string) => {
    if (isLoading) {
      return (
        <div className={`grid ${cols} gap-6`}>
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-96 w-full rounded-2xl" />)}
        </div>
      );
    }
    if (rows.length === 0) {
      return <p className="text-center text-muted-foreground py-12">No packages available right now. Check back soon.</p>;
    }
    return (
      <>
        <h2 className="text-xl md:text-2xl font-bold mb-6">{sectionLabel}</h2>
        <div className={`grid ${cols} gap-6`}>
          {rows.map((p) => <PackageCard key={p.id} pkg={p} onSelect={onSelect} />)}
        </div>
      </>
    );
  };

  return (
    <Layout>
      <section className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-accent/5 to-secondary/5 py-16 md:py-24">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-primary/20 blur-3xl" />
          <div className="absolute bottom-10 right-20 w-48 h-48 rounded-full bg-accent/20 blur-3xl" />
        </div>
        <div className="container relative text-center">
          <h1 className="text-3xl md:text-5xl font-bold text-foreground mb-4">
            Let EthioRealEstates Build Your Business
          </h1>
          <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto">
            Reach millions of property seekers across Ethiopia. Choose a plan that fits your goals and
            start generating quality leads today.
          </p>
        </div>
      </section>

      <section className="container py-12 md:py-16">
        <Tabs defaultValue="agency" className="w-full">
          <div className="flex justify-center mb-10">
            <TabsList className="grid grid-cols-3 w-full max-w-md h-12">
              <TabsTrigger value="agency" className="gap-1.5 text-xs sm:text-sm">
                <Building2 className="h-4 w-4" /> Agencies
              </TabsTrigger>
              <TabsTrigger value="developer" className="gap-1.5 text-xs sm:text-sm">
                <HardHat className="h-4 w-4" /> Developers
              </TabsTrigger>
              <TabsTrigger value="individual" className="gap-1.5 text-xs sm:text-sm">
                <User className="h-4 w-4" /> Individuals
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="agency">
            {renderGrid(grouped.agency, 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4', 'Advertise for Agencies')}
          </TabsContent>
          <TabsContent value="developer">
            {renderGrid(grouped.developer, 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4', 'Advertise for Developers')}
          </TabsContent>
          <TabsContent value="individual">
            {renderGrid(grouped.individual, 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5', 'Advertise as an Individual')}
          </TabsContent>
        </Tabs>
      </section>

      
      <PackageInquiryDialog open={!!inquiryPkg} onOpenChange={(o) => !o && setInquiryPkg(null)} pkg={inquiryPkg as any} />
    </Layout>
  );
}
